(() => {
  if (window.IMFilters) return;
  const dimensions = new Set(['filter.p.m.custom.width_cm', 'filter.p.m.custom.ilgis_cm', 'filter.p.m.custom.aukstis_cm']);
  const number = value => {
    const text = String(value ?? '').trim().replace(',', '.');
    return /^\d+(?:\.\d+)?$/.test(text) ? Number(text) : NaN;
  };
  const display = value => String(Math.round(value * 100) / 100);
  const boundKey = (param, side) => `im_range_${param.split('.').pop()}_${side}`;
  const unique = values => [...new Set(values)];
  const currentValues = (params, param) => params.getAll(param).flatMap(value => value.split(','));
  const normalizeDimensionParams = params => {
    for (const param of dimensions) {
      const values = unique(currentValues(params, param)).filter(value => value !== '');
      params.delete(param);
      values.forEach(value => params.append(param, value));
    }
  };
  let dimensionIndex;
  const loadDimensionIndex = () => {
    if (dimensionIndex) return dimensionIndex;
    dimensionIndex = (async () => {
      if (!location.pathname.includes('/collections/')) throw new Error('Collection context required');
      const source = new URL(location.href);
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('view', 'im-dimension-index');
      if (source.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', source.searchParams.get('preview_theme_id'));
      const products = [];
      let pages = 1;
      let total;
      for (let page = 1; page <= pages; page++) {
        url.searchParams.set('page', page);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Dimension index request failed');
        const data = await response.json();
        if (!Array.isArray(data.products) || data.page !== page || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100) throw new Error('Incomplete dimension index');
        if (page === 1) { pages = data.pages; total = data.total; }
        if (data.pages !== pages || data.total !== total) throw new Error('Collection changed during index loading');
        products.push(...data.products);
      }
      if (products.length !== total) throw new Error('Incomplete dimension index');
      const index = {};
      [...dimensions].forEach((param, column) => {
        index[param] = unique(products.map(product => String(product[column] ?? '')).filter(value => Number.isFinite(number(value)))).sort((a, b) => number(a) - number(b));
      });
      return index;
    })();
    return dimensionIndex;
  };

  class IMFilterRange extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.loading) return;
      this.isPrice = this.dataset.mode === 'price';
      this.param = this.dataset.param;
      this.fields = [this.querySelector('[data-range-min]'), this.querySelector('[data-range-max]')];
      this.sliders = [this.querySelector('[data-range-low]'), this.querySelector('[data-range-high]')];
      this.message = this.querySelector('[data-range-message]');
      this.resetButton = this.querySelector('[data-range-reset]');
      this.options = this.isPrice ? [] : JSON.parse(this.querySelector('[data-range-options]').textContent);
      this.options.forEach(option => { option.numeric = number(option.value); });
      const url = new URLSearchParams(location.search);
      this.original = currentValues(url, this.param);
      if (!this.isPrice && this.options.length >= 100 && this.dataset.complete !== 'true') {
        this.loading = true;
        this.setAttribute('aria-busy', 'true');
        const note = document.createElement('p');
        note.className = 'im-range-fallback';
        note.textContent = this.dataset.loading;
        this.after(note);
        loadDimensionIndex().then(index => {
          const values = index[this.param];
          if (!values?.length) throw new Error('No numeric dimensions');
          this.querySelector('[data-range-options]').textContent = JSON.stringify(values.map(value => ({value, label: value, active: this.original.includes(value)})));
          this.dataset.complete = 'true';
          this.loading = false;
          this.removeAttribute('aria-busy');
          note.remove();
          this.connectedCallback();
          renderChips();
        }).catch(() => {
          this.loading = false;
          this.removeAttribute('aria-busy');
          note.textContent = this.dataset.fallback;
        });
        return;
      }
      if (!this.isPrice && (!this.options.length || this.options.some(option => !Number.isFinite(option.numeric)))) {
        const note = document.createElement('p');
        note.className = 'im-range-fallback';
        note.textContent = this.dataset.fallback;
        this.after(note);
        return;
      }
      this.min = this.isPrice ? 0 : Math.min(...this.options.map(option => option.numeric));
      this.max = this.isPrice ? Number(this.dataset.max) : Math.max(...this.options.map(option => option.numeric));
      if (!Number.isFinite(this.max) || this.max < this.min) return;
      this.edited = false;
      if (!this.isPrice) {
        const selected = this.options.filter(option => option.active).map(option => option.numeric);
        const hasBounds = this.original.length && (url.has(boundKey(this.param, 'min')) || url.has(boundKey(this.param, 'max')));
        this.explicitBounds = Boolean(hasBounds);
        this.fields[0].value = hasBounds ? url.get(boundKey(this.param, 'min')) ?? '' : selected.length ? display(Math.min(...selected)) : '';
        this.fields[1].value = hasBounds ? url.get(boundKey(this.param, 'max')) ?? '' : selected.length ? display(Math.max(...selected)) : '';
        this.parentElement.classList.add('im-range-ready');
      }
      this.hidden = false;
      this.ready = true;
      this.querySelector('.im-range-sliders').hidden = false;
      this.resetButton.hidden = false;
      this.querySelector('[data-range-bounds]').textContent = `${display(this.min)}–${display(this.max)} ${this.dataset.unit}`;
      this.fields.forEach(field => {
        field.placeholder = display(field === this.fields[0] ? this.min : this.max);
        field.addEventListener('input', () => { this.edited = true; this.update(); this.sync(); });
        field.addEventListener('keydown', event => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          window.IMFilters.apply(this.closest('form'));
        });
        field.addEventListener('blur', () => { if (this.valid()) this.fields.forEach(input => { if (input.value.trim()) input.value = display(number(input.value)); }); });
      });
      this.sliders.forEach((slider, index) => {
        slider.min = this.min;
        slider.max = this.max;
        slider.step = this.isPrice ? '.01' : this.options.some(option => option.numeric % 1) ? '.1' : '1';
        slider.disabled = this.max === this.min;
        slider.addEventListener('input', () => {
          const other = Number(this.sliders[1 - index].value);
          const value = index === 0 ? Math.min(Number(slider.value), other) : Math.max(Number(slider.value), other);
          this.fields[0].value = display(index === 0 ? value : Number(this.sliders[0].value));
          this.fields[1].value = display(index === 1 ? value : Number(this.sliders[1].value));
          this.edited = true;
          this.update();
          this.sync();
        });
        slider.addEventListener('pointerdown', () => { this.sliders.forEach(input => { input.style.zIndex = input === slider ? '2' : '1'; }); });
      });
      this.resetButton.addEventListener('click', event => {
        event.stopImmediatePropagation();
        this.fields.forEach(field => { field.value = ''; });
        this.edited = true;
        this.update();
        this.sync();
      }, { capture: true });
      this.update();
    }

    bounds() {
      return [this.fields[0].value.trim() === '' ? this.min : number(this.fields[0].value), this.fields[1].value.trim() === '' ? this.max : number(this.fields[1].value)];
    }

    valid() {
      const [low, high] = this.bounds();
      return Number.isFinite(low) && Number.isFinite(high) && low <= high;
    }

    hasMatches() {
      const [low, high] = this.bounds();
      return this.isPrice || this.options.some(option => option.numeric >= low && option.numeric <= high);
    }

    update() {
      const [low, high] = this.bounds();
      const valid = this.valid();
      this.fields.forEach(field => field.setAttribute('aria-invalid', String(!valid)));
      const clamp = value => Math.max(this.min, Math.min(this.max, value));
      this.sliders[0].value = clamp(Number.isFinite(low) ? low : this.min);
      this.sliders[1].value = clamp(Number.isFinite(high) ? high : this.max);
      const span = this.max - this.min || 1;
      const track = this.querySelector('.im-range-track');
      track.style.setProperty('--range-from', `${(Number(this.sliders[0].value) - this.min) / span * 100}%`);
      track.style.setProperty('--range-to', `${(Number(this.sliders[1].value) - this.min) / span * 100}%`);
      this.sliders.forEach((slider, index) => {
        slider.setAttribute('aria-valuetext', `${display(Number(slider.value))} ${this.dataset.unit}`);
        slider.setAttribute(index === 0 ? 'aria-valuemax' : 'aria-valuemin', this.sliders[1 - index].value);
      });
      const empty = !this.hasMatches();
      this.message.textContent = !valid ? this.dataset.invalid : empty ? this.dataset.empty : '';
    }

    sync() {
      document.querySelectorAll('im-filter-range').forEach(range => {
        if (range === this || !range.ready || range.param !== this.param) return;
        this.fields.forEach((field, index) => { range.fields[index].value = field.value; });
        range.edited = true;
        range.update();
      });
    }

    serialize(params) {
      const [low, high] = this.bounds();
      if (this.isPrice) {
        [this.dataset.minParam, this.dataset.maxParam].forEach((key, index) => {
          params.delete(key);
          if (this.fields[index].value.trim()) params.set(key, display(index === 0 ? low : high));
        });
        return;
      }
      if (!this.edited) return;
      params.delete(this.param);
      params.delete(`${this.param}.gte`);
      params.delete(`${this.param}.lte`);
      ['min', 'max'].forEach(side => params.delete(boundKey(this.param, side)));
      if (!this.fields.some(field => field.value.trim())) return;
      if (low <= this.min && high >= this.max) return;
      const values = unique(this.options.filter(option => option.numeric >= low && option.numeric <= high).map(option => String(option.value)));
      values.forEach(value => params.append(this.param, value));
      this.fields.forEach((field, index) => {
        if (field.value.trim()) params.set(boundKey(this.param, index === 0 ? 'min' : 'max'), display(index === 0 ? low : high));
      });
    }
  }

  const buildURL = (form, sortValue) => {
    const url = new URL(location.href);
    const params = url.searchParams;
    params.delete('page');
    normalizeDimensionParams(params);
    const ranges = [...form.querySelectorAll('im-filter-range')].filter(range => range.ready);
    const invalid = ranges.find(range => !range.valid() || !range.hasMatches());
    if (invalid) { invalid.closest('details').open = true; invalid.fields[0].focus(); return null; }
    const managed = new Set(ranges.filter(range => !range.isPrice).map(range => range.param));
    const keys = unique([...form.querySelectorAll('input[name], select[name]')].filter(input => !input.closest('im-filter-range') && !managed.has(input.name)).map(input => input.name));
    keys.forEach(key => {
      params.delete(key);
      const inputs = [...form.elements].filter(input => input.name === key && !input.disabled && !input.closest('im-filter-range'));
      const values = unique(inputs.filter(input => !['checkbox', 'radio'].includes(input.type) || input.checked).map(input => input.value).filter(value => value !== ''));
      values.forEach(value => params.append(key, value));
    });
    ranges.forEach(range => range.serialize(params));
    if (sortValue !== undefined) params.set('sort_by', sortValue);
    return url;
  };

  const cleanRemoval = href => {
    const url = new URL(href, location.href);
    const before = new URLSearchParams(location.search);
    for (const param of dimensions) {
      const oldValues = unique(currentValues(before, param)).sort().join(',');
      const nextValues = unique(currentValues(url.searchParams, param)).sort().join(',');
      if (oldValues !== nextValues || !url.searchParams.has(param)) {
        ['min', 'max'].forEach(side => url.searchParams.delete(boundKey(param, side)));
      }
    }
    url.searchParams.delete('page');
    normalizeDimensionParams(url.searchParams);
    return url.href;
  };

  window.IMFilters = {
    buildURL,
    cleanRemoval,
    apply(form, sortValue) {
      if (!form) return;
      const url = buildURL(form, sortValue);
      if (url) location.assign(url.href);
    }
  };
  customElements.define('im-filter-range', IMFilterRange);

  document.addEventListener('click', event => {
    const button = event.target.closest('#apply-filters-desktop, #apply-filters-mobile, [data-im-apply]');
    if (!button) return;
    event.preventDefault();
    const form = button.closest('form') || button.closest('facet-filters-form')?.querySelector('form');
    window.IMFilters.apply(form);
  });

  const renderChips = () => {
    const params = new URLSearchParams(location.search);
    document.querySelectorAll('form[data-im-filters]').forEach(form => {
      form.querySelector('.im-range-chips')?.remove();
      const chips = document.createElement('div');
      chips.className = 'im-range-chips';
      for (const range of form.querySelectorAll('im-filter-range')) {
        if (!range.ready || range.isPrice || !params.has(range.param)) continue;
        const values = currentValues(params, range.param).map(number).filter(Number.isFinite);
        const low = params.get(boundKey(range.param, 'min')) ?? (values.length ? display(Math.min(...values)) : '');
        const high = params.get(boundKey(range.param, 'max')) ?? (values.length ? display(Math.max(...values)) : '');
        const url = new URL(location.href);
        url.searchParams.delete(range.param);
        ['min', 'max'].forEach(side => url.searchParams.delete(boundKey(range.param, side)));
        url.searchParams.delete('page');
        const chip = document.createElement('a');
        chip.className = 'im-range-chip';
        chip.href = url.href;
        const precise = range.explicitBounds || range.options.filter(option => option.active).length === values.length && unique(values).length === range.options.filter(option => option.numeric >= Number(low) && option.numeric <= Number(high)).length;
        const selected = precise ? `${low || display(range.min)}–${high || display(range.max)}` : currentValues(params, range.param).join(', ');
        chip.textContent = `${range.dataset.label}: ${selected} ${range.dataset.unit} ×`;
        chip.setAttribute('aria-label', `${range.dataset.resetLabel}: ${range.dataset.label} ${selected} ${range.dataset.unit}`);
        chips.append(chip);
        document.querySelectorAll('facet-remove a[href]').forEach(link => {
          const target = new URL(link.href, location.href);
          if (unique(currentValues(target.searchParams, range.param)).sort().join(',') !== unique(currentValues(params, range.param)).sort().join(',') && target.searchParams.has('filter.v.price.gte') === params.has('filter.v.price.gte') && link.classList.contains('active-facets__button')) link.closest('facet-remove').classList.add('im-range-active-value');
        });
      }
      if (chips.childElementCount) (form.querySelector('.mobile-facets__main') || form).prepend(chips);
    });
  };
  renderChips();
  document.querySelectorAll('.pagination a[href]').forEach(link => {
    const url = new URL(link.href, location.href);
    const params = new URLSearchParams(location.search);
    params.forEach((value, key) => { if (key.startsWith('im_range_')) url.searchParams.set(key, value); });
    link.href = url.href;
  });
})();
